"use client";

import { useState, useActionState } from "react";
import { useTranslations } from "next-intl";
import { submitReviewAction } from "@/lib/actions/reviews";
import type { SubmitReviewState } from "@/lib/actions/reviews";
import { BusyLabel } from "@/components/ui/Spinner";

interface Props {
  professionalId: string;
  proSlug: string;
  hasReviewed: boolean;
  prefillRole?: string | null;
}

const STAR_LABEL_KEYS = ["starPoor", "starFair", "starGood", "starVeryGood", "starExcellent"] as const;

export default function ReviewForm({ professionalId, proSlug, hasReviewed, prefillRole }: Props) {
  const t = useTranslations("profile");
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);

  const [state, formAction, isPending] = useActionState<SubmitReviewState, FormData>(
    submitReviewAction,
    undefined,
  );

  if (hasReviewed || state?.status === "success") {
    return (
      <div className="rounded-card border border-card-line bg-surface px-21 py-13">
        <p className="text-sm text-gray-600">
          {state?.status === "success"
            ? t("reviewSuccess")
            : t("reviewAlready")}
        </p>
      </div>
    );
  }

  const displayRating = hovered || rating;

  return (
    <form action={formAction} className="space-y-13">
      <input type="hidden" name="professional_id" value={professionalId} />
      <input type="hidden" name="pro_slug"        value={proSlug} />
      <input type="hidden" name="rating"           value={rating} />

      {/* Star selector */}
      <div>
        <p className="mb-8 text-xs font-semibold uppercase tracking-[0.08em] text-gray-600">
          {t("reviewRating")} <span className="text-red-500" aria-hidden="true">*</span>
        </p>
        <div
          role="group"
          aria-label={t("reviewStarRatingAria")}
          className="flex items-center gap-5"
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              aria-label={t("reviewStarAria", { count: star, label: t(STAR_LABEL_KEYS[star - 1]) })}
              aria-pressed={rating === star}
              onClick={() => setRating(star)}
              onMouseEnter={() => setHovered(star)}
              onMouseLeave={() => setHovered(0)}
              className="text-[1.75rem] leading-none transition-transform hover:scale-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gold"
            >
              <span
                aria-hidden="true"
                className={displayRating >= star ? "text-gold" : "text-gray-300"}
              >
                ★
              </span>
            </button>
          ))}
          {displayRating > 0 && (
            <span className="ml-8 text-xs text-gray-500">
              {t(STAR_LABEL_KEYS[displayRating - 1])}
            </span>
          )}
        </div>
      </div>

      {/* Role + Company */}
      <div className="flex gap-8">
        <div className="flex-1">
          <label
            htmlFor="review-role"
            className="mb-8 block text-xs font-semibold uppercase tracking-[0.08em] text-gray-600"
          >
            {t("reviewRole")} <span className="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="review-role"
            name="author_role"
            type="text"
            required
            defaultValue={prefillRole ?? undefined}
            placeholder={t("reviewRolePlaceholder")}
            disabled={isPending}
            className="w-full rounded-control border border-navy/20 bg-white px-13 py-8 text-sm text-navy placeholder:text-gray-500 focus:border-navy/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-60"
          />
        </div>
        <div className="flex-1">
          <label
            htmlFor="review-company"
            className="mb-8 block text-xs font-semibold uppercase tracking-[0.08em] text-gray-600"
          >
            {t("reviewCompany")} <span className="font-normal text-gray-500">{t("optional")}</span>
          </label>
          <input
            id="review-company"
            name="author_company"
            type="text"
            placeholder={t("reviewCompanyPlaceholder")}
            disabled={isPending}
            className="w-full rounded-control border border-navy/20 bg-white px-13 py-8 text-sm text-navy placeholder:text-gray-500 focus:border-navy/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-60"
          />
        </div>
      </div>

      {/* Review text */}
      <div>
        <label
          htmlFor="review-text"
          className="mb-8 block text-xs font-semibold uppercase tracking-[0.08em] text-gray-600"
        >
          {t("reviewText")} <span className="font-normal text-gray-500">{t("optional")}</span>
        </label>
        <textarea
          id="review-text"
          name="text"
          rows={4}
          placeholder={t("reviewTextPlaceholder")}
          disabled={isPending}
          className="w-full resize-none rounded-control border border-navy/20 bg-white px-13 py-8 text-sm text-navy placeholder:text-gray-500 focus:border-navy/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:opacity-60"
        />
      </div>

      {/* Error */}
      {state?.status === "error" && (
        <p className="text-xs text-red-600" role="alert">{state.message}</p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isPending || rating === 0}
        className="rounded-control bg-navy px-21 py-10 text-xs font-semibold uppercase tracking-[0.06em] text-white transition-colors hover:bg-navy/80 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <BusyLabel busy={isPending}>{isPending ? t("reviewSubmitting") : t("reviewSubmit")}</BusyLabel>
      </button>
    </form>
  );
}
